import {
  AnalysisTemplateGraphQL,
  AnalysisTemplateConfigVariableGraphQL,
} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {ARCHIVE_RECORD, ARCHIVE_RECORD_GQL} from 'archive/__tests__/ArchiveRecord-fixtures';
import {Permissions} from 'auth/PermissionConstants';
import SampleType from 'constants/SampleType';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AutomationInputFileConfig} from 'lab-automation/automation-files/LabAutomationConstants';
import {
  ASSAY_TYPES,
  AssayRunSchema,
  AssaySchema,
  ResultSchema,
} from 'results/assay-schemas/AssaySchemaConstants';
import {
  BatchSampleLinkSchemaField,
  BioentitySampleLinkSchemaField,
  FieldType,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {ORGANIZATION} from 'test/fixtures/parties/OrganizationFixtures';

import {ResultSchemaGraphQL} from '../../../graphql-fragments/results/schemas/__generated/ResultSchemaGraphQL';
import {RunSchemaGraphQL} from '../../../graphql-fragments/runs/schemas/__generated/RunSchemaGraphQL';
import {SchemaPermissionsGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaPermissionsGraphQL';

export const ASSAY_SCHEMA_PERMISSIONS: Permissions = {
  admin: false,
  appendable: true,
  archivable: true,
  definitionDeletable: true,
  definitionEditable: true,
  definitionListable: true,
  objectsCreatable: true,
  objectsRegisterable: true,
  readable: true,
  unarchivable: false,
  writable: true,
};

export const ASSAY_SCHEMA_PERMISSIONS_GQL: SchemaPermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: true,
  archivable: true,
  definitionDeletable: true,
  definitionEditable: true,
  definitionListable: true,
  objectsCreatable: true,
  objectsRegisterable: true,
  readable: true,
  unarchivable: false,
  writable: true,
};

export const ASSAY_SCHEMA_FIELD: SchemaField = {
  archiveRecord: null,
  id: 1,
  dbId: 1,
  apiId: 'tsf_111',
  name: 'my_count',
  systemName: 'my_count',
  displayName: 'My Count',
  fieldType: FieldType.INTEGER,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
  description: null,
  tooltipText: 'This is a tooltip',
  unitApiIdentifier: null,
};

export const ARCHIVED_FIELD: SchemaField = {
  id: 2,
  dbId: 2,
  apiId: 'tsf_112',
  name: 'my_number',
  systemName: 'my_number',
  displayName: 'My Number',
  fieldType: FieldType.INTEGER,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
  archiveRecord: {
    purpose: 'Archived',
  },
};

export const BOOLEAN_FIELD: SchemaField = {
  archiveRecord: null,
  id: 3,
  dbId: 3,
  apiId: 'tsf_113',
  name: 'my_bool',
  systemName: 'my_bool',
  displayName: 'My Boolean',
  fieldType: FieldType.BOOLEAN,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

export const DATETIME_FIELD: SchemaField = {
  archiveRecord: null,
  id: 4,
  dbId: 4,
  apiId: 'tsf_114',
  name: 'my_datetime',
  systemName: 'my_datetime',
  displayName: 'My Datetime',
  fieldType: FieldType.DATETIME,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

export const JSON_FIELD: SchemaField = {
  archiveRecord: null,
  id: 5,
  dbId: 5,
  apiId: 'tsf_115',
  name: 'my_json',
  systemName: 'my_json',
  displayName: 'My Json',
  fieldType: FieldType.JSON,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

// Analysis template generated fields
export const ANALYSIS_TEMPLATE_STRING_FIELD: SchemaField = {
  archiveRecord: null,
  id: 8,
  dbId: 8,
  apiId: 'tsf_118',
  name: 'template_string_field',
  systemName: 'anatmplvar_stringv_ar', // Converted from anatmplvar_stringVar
  displayName: 'Template String Field',
  fieldType: FieldType.TEXT,
  isRequired: true,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

export const ANALYSIS_TEMPLATE_NUMBER_FIELD: SchemaField = {
  archiveRecord: null,
  id: 9,
  dbId: 9,
  apiId: 'tsf_119',
  name: 'template_number_field',
  systemName: 'anatmplvar_numberv_ar', // Converted from anatmplvar_numberVar
  displayName: 'Template Number Field',
  fieldType: FieldType.FLOAT,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

export const ANALYSIS_TEMPLATE_INTEGER_FIELD: SchemaField = {
  archiveRecord: null,
  id: 10,
  dbId: 10,
  apiId: 'tsf_120',
  name: 'template_integer_field',
  systemName: 'anatmplvar_integerv_ar', // Converted from anatmplvar_integerVar
  displayName: 'Template Integer Field',
  fieldType: FieldType.INTEGER,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

export const ANALYSIS_TEMPLATE_BOOLEAN_FIELD: SchemaField = {
  archiveRecord: null,
  id: 11,
  dbId: 11,
  apiId: 'tsf_121',
  name: 'template_boolean_field',
  systemName: 'anatmplvar_booleanv_ar', // Converted from anatmplvar_booleanVar
  displayName: 'Template Boolean Field',
  fieldType: FieldType.BOOLEAN,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

export const ANALYSIS_TEMPLATE_DATE_FIELD: SchemaField = {
  archiveRecord: null,
  id: 12,
  dbId: 12,
  apiId: 'tsf_122',
  name: 'template_date_field',
  systemName: 'anatmplvar_datev_ar', // Converted from anatmplvar_dateVar
  displayName: 'Template Date Field',
  fieldType: FieldType.DATETIME,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

export const ANALYSIS_TEMPLATE_PLATE_ARRAY_FIELD: SchemaField = {
  archiveRecord: null,
  id: 13,
  dbId: 13,
  apiId: 'tsf_123',
  name: 'template_plate_array_field',
  systemName: 'anatmplvar_platearrayv_ar', // Converted from anatmplvar_plateArrayVar
  displayName: 'Template Plate Array Field',
  fieldType: FieldType.STORABLE_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: true,
  schemaFieldSelectorId: null,
  requiredLink: {
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: null,
    tagSchema: null,
  },
};

// Batches are deprecated: we are preventing new batch link field creation and will soon make all batch link
// fields inaccessible in the UI (go/batch-deprecation-2025). Unless necessary to test deprecation, avoid
// adding tests that support batch link fields.
export const BATCH_LINK_FIELD: SchemaField = {
  archiveRecord: null,
  id: 8471,
  dbId: 8471,
  apiId: 'tsf_001_batch_link',
  name: 'ft_batch_link',
  systemName: 'ft_batch_link',
  displayName: 'ft_batch_link',
  fieldType: FieldType.BATCH_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isIntegration: false,
  isMulti: true,
  isParentLink: false,
  isSnapshot: false,
  schemaFieldSelectorId: null,
  requiredLink: {
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: null,
    tagSchema: null,
  },
};

export const ENTRY_LINK_FIELD: SchemaField = {
  archiveRecord: null,
  id: 54,
  dbId: 54,
  apiId: 'tsf_001_entry_link',
  name: 'ft_entry_link',
  systemName: 'ft_entry_link',
  displayName: 'ft_entry_link',
  fieldType: FieldType.ENTRY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isIntegration: false,
  isMulti: true,
  isParentLink: false,
  isSnapshot: false,
  schemaFieldSelectorId: null,
  requiredLink: {
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: null,
    tagSchema: null,
  },
};

export const RUN_LINK_FIELD: SchemaField = {
  archiveRecord: null,
  id: 3,
  dbId: 3,
  apiId: 'tsf_113_run_link',
  name: 'run_link_field',
  systemName: 'run_link_field',
  displayName: 'Run Link Field',
  fieldType: FieldType.ASSAY_RUN_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: {
    assaySchema: null,
  },
};

export const SAMPLES_SCHEMA_FIELD: BioentitySampleLinkSchemaField = {
  fieldType: FieldType.SAMPLE_LINK,
  requiredLink: {
    sampleType: SampleType.BIOENTITY,
    tagSchema: null,
  },
  archiveRecord: null,
  id: 7,
  dbId: 7,
  apiId: 'tsf_117',
  name: 'sample_entity',
  systemName: 'sample_entity',
  displayName: 'Sample Entity',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  description: null,
  tooltipText: null,
  unitApiIdentifier: null,
};

export const REQUIRED_FIELD: SchemaField = {
  archiveRecord: null,
  id: 1002,
  dbId: 1002,
  apiId: 'tsf_1002_required_field',
  name: 'Text Field',
  systemName: 'text_field',
  displayName: 'Text Field',
  fieldType: FieldType.TEXT,
  isRequired: true,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

// Batches are deprecated. Only needed for tests to ensure that users cannot input a new batch link field.
// Unless necessary for go/batch-deprecation-2025, avoid adding tests that support batch link fields.
export const BATCH_SAMPLES_SCHEMA_FIELD: BatchSampleLinkSchemaField = {
  ...BATCH_LINK_FIELD,
  // Overwrite some properties to match the BatchSampleLinkSchemaField type
  fieldType: FieldType.SAMPLE_LINK,
  requiredLink: {
    sampleType: SampleType.BATCH,
    batchSchema: null,
  },
};

const DEFAULT_RUN_SCHEMA_PROPS = {
  automationOutputFileConfigs: [],
  automationInputFileConfigs: [],
  robotInstructionsConfig: null,
  allowCreateRunFromNlad: true,
  allowInsertRunFromInbox: true,
  permissions: ASSAY_SCHEMA_PERMISSIONS,
  analysisTemplate: null,
  analysisTemplateVariableMapping: null,
  analysisTemplateDefaultFiltersMapping: null,
  adapterId: null,
  shouldRecordEquipment: false,
};

export const FLOW_RUN_SCHEMA: AssayRunSchema & {
  robotInstructionsConfig: AutomationInputFileConfig | null;
} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_1',
  name: 'Flow Cytometry Run',
  requestTaskSchemaIds: [],
  sampleGroupSchema: null,
  sqlIdentifier: 'flow_cytometry_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [ASSAY_SCHEMA_FIELD],
  fields: [ASSAY_SCHEMA_FIELD],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
  automationOutputFileConfigs: [],
  automationInputFileConfigs: [],
  robotInstructionsConfig: null,
  allowInsertRunFromInbox: true,
  allowCreateRunFromNlad: true,
  shouldRecordEquipment: false,
};

// Analysis template config variables for testing
export const ANALYSIS_TEMPLATE_STRING_CONFIG_VAR: AnalysisTemplateConfigVariableGraphQL = {
  __typename: 'AnalysisTemplateVariableNode',
  id: 'anatmplvar_stringVar',
  name: 'stringVar',
  configVariableKey: 'stringVar',
  variableTypeConfigSpec: {kind: 'string', required: true},
  pipelineStepDomain: {
    __typename: 'PipelineStepDomainNode',
    id: 'step_123',
    stepType: PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  },
};

export const ANALYSIS_TEMPLATE_NUMBER_CONFIG_VAR: AnalysisTemplateConfigVariableGraphQL = {
  __typename: 'AnalysisTemplateVariableNode',
  id: 'anatmplvar_numberVar',
  name: 'numberVar',
  configVariableKey: 'numberVar',
  variableTypeConfigSpec: {kind: 'number', required: false},
  pipelineStepDomain: {
    __typename: 'PipelineStepDomainNode',
    id: 'step_124',
    stepType: PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  },
};

export const ANALYSIS_TEMPLATE_INTEGER_CONFIG_VAR: AnalysisTemplateConfigVariableGraphQL = {
  __typename: 'AnalysisTemplateVariableNode',
  id: 'anatmplvar_integerVar',
  name: 'integerVar',
  configVariableKey: 'integerVar',
  variableTypeConfigSpec: {kind: 'integer', required: false},
  pipelineStepDomain: {
    __typename: 'PipelineStepDomainNode',
    id: 'step_125',
    stepType: PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  },
};

export const ANALYSIS_TEMPLATE_BOOLEAN_CONFIG_VAR: AnalysisTemplateConfigVariableGraphQL = {
  __typename: 'AnalysisTemplateVariableNode',
  id: 'anatmplvar_booleanVar',
  name: 'booleanVar',
  configVariableKey: 'booleanVar',
  variableTypeConfigSpec: {kind: 'boolean', required: false},
  pipelineStepDomain: {
    __typename: 'PipelineStepDomainNode',
    id: 'step_126',
    stepType: PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  },
};

export const ANALYSIS_TEMPLATE_DATE_CONFIG_VAR: AnalysisTemplateConfigVariableGraphQL = {
  __typename: 'AnalysisTemplateVariableNode',
  id: 'anatmplvar_dateVar',
  name: 'dateVar',
  configVariableKey: 'dateVar',
  variableTypeConfigSpec: {kind: 'date', required: false},
  pipelineStepDomain: {
    __typename: 'PipelineStepDomainNode',
    id: 'step_127',
    stepType: PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  },
};

export const ANALYSIS_TEMPLATE_PLATE_ARRAY_CONFIG_VAR: AnalysisTemplateConfigVariableGraphQL = {
  __typename: 'AnalysisTemplateVariableNode',
  id: 'anatmplvar_plateArrayVar',
  name: 'plateArrayVar',
  configVariableKey: 'plateArrayVar',
  variableTypeConfigSpec: {
    kind: 'array',
    elementSpec: {kind: 'domain', types: ['PLATE'], required: true},
    required: false,
  },
  pipelineStepDomain: {
    __typename: 'PipelineStepDomainNode',
    id: 'step_128',
    stepType: PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  },
};

export const ANALYSIS_TEMPLATE_FILE_CONFIG_VAR: AnalysisTemplateConfigVariableGraphQL = {
  __typename: 'AnalysisTemplateVariableNode',
  id: 'anatmplvar_fileVar',
  name: 'fileVar',
  configVariableKey: 'fileVar',
  variableTypeConfigSpec: {kind: 'domain', types: ['FILE'], required: false},
  pipelineStepDomain: {
    __typename: 'PipelineStepDomainNode',
    id: 'step_129',
    stepType: PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  },
};

export const ASSAY_RUN_ANALYSIS_TEMPLATE: AnalysisTemplateGraphQL = {
  __typename: 'AnalysisTemplateNode',
  id: 'anatmpl_test123',
  name: 'Test Assay Run Analysis Template',
  configVariables: [
    ANALYSIS_TEMPLATE_STRING_CONFIG_VAR,
    ANALYSIS_TEMPLATE_NUMBER_CONFIG_VAR,
    ANALYSIS_TEMPLATE_INTEGER_CONFIG_VAR,
    ANALYSIS_TEMPLATE_BOOLEAN_CONFIG_VAR,
    ANALYSIS_TEMPLATE_DATE_CONFIG_VAR,
    ANALYSIS_TEMPLATE_PLATE_ARRAY_CONFIG_VAR,
    ANALYSIS_TEMPLATE_FILE_CONFIG_VAR,
  ],
  dataFrameVariables: [],
  archiveRecord: null,
  createdAt: '2023-01-01T00:00:00Z',
  modifiedAt: '2023-01-01T00:00:00Z',
  creator: {
    __typename: 'UserNode',
    id: 'ent_ERLJ1XiQ',
    name: 'Richard Feynman',
  },
  execution: {
    __typename: 'PipelineExecutionNode',
    id: 'anaexc_test123',
    steps: [],
  },
  steps: [],
  views: [],
  permissions: {
    __typename: 'PermissionsNode',
    writable: true,
    archivable: true,
    movable: true,
    unarchivable: false,
  },
  templateCollection: {
    __typename: 'TemplateCollectionNode',
    id: 'tmplcol_ghs2DS2k',
    name: 'Test template collection',
  },
  editUrl: '/ent_ERLJ1XiQ/analysis-templates/anatmpl_test123',
  publishedAt: null,
};

const DEFAULT_RUN_SCHEMA_GQL_PROPS = {
  automationInputFileConfigsJson: [],
  automationOutputFileConfigsJson: [],
  robotInstructionsConfigJson: null,
  allowCreateRunFromNlad: true,
  allowInsertRunFromInbox: true,
  permissions: ASSAY_SCHEMA_PERMISSIONS_GQL,
  analysisTemplate: null,
  analysisTemplateVariableMapping: null,
  analysisTemplateDefaultFiltersMapping: null,
  adapterId: null,
  shouldRecordEquipment: false,
};

export const FLOW_RUN_SCHEMA_GQL: RunSchemaGraphQL = {
  ...DEFAULT_RUN_SCHEMA_GQL_PROPS,
  __typename: 'RunSchemaNode',
  id: 'assaysch_1',
  name: 'Flow Cytometry Run',
  sqlIdentifier: 'flow_cytometry_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organizationJson: ORGANIZATION,
  allFieldsJson: [ASSAY_SCHEMA_FIELD],
  fieldsJson: [ASSAY_SCHEMA_FIELD],
  archiveRecord: null,
  automationInputFileConfigsJson: [],
  automationOutputFileConfigsJson: [],
  robotInstructionsConfigJson: null,
  allowInsertRunFromInbox: true,
  allowCreateRunFromNlad: true,
  shouldRecordEquipment: false,
};

export const CELL_SAMPLE_PREP_RUN_SCHEMA: AssayRunSchema & {
  robotInstructionsConfig: AutomationInputFileConfig | null;
} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_2',
  name: 'Cell Sample Prep Run',
  requestTaskSchemaIds: [],
  sampleGroupSchema: null,
  sqlIdentifier: 'cell_sample_prep_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
};

export const NGS_RUN_SCHEMA: AssayRunSchema & {robotInstructionsConfig: AutomationInputFileConfig | null} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_3',
  name: 'NGS Run',
  requestTaskSchemaIds: [],
  sampleGroupSchema: null,
  sqlIdentifier: 'ngs_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
};

export const FLOW_CYTOMETRY_RESULT: ResultSchema = {
  id: 'assaysch_4',
  name: 'Flow Cytometry Result',
  requestTaskSchemaIds: [],
  sampleGroupSchema: null,
  sqlIdentifier: 'flow_cytometry_result',
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  organization: ORGANIZATION,
  allFields: [],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

export const RUN_SCHEMA_WITH_ANALYSIS_TEMPLATE: AssayRunSchema & {
  robotInstructionsConfig: AutomationInputFileConfig | null;
} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_with_template',
  name: 'Run Schema with Analysis Template',
  requestTaskSchemaIds: [],
  sampleGroupSchema: null,
  sqlIdentifier: 'run_with_template',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [
    ASSAY_SCHEMA_FIELD,
    ANALYSIS_TEMPLATE_STRING_FIELD,
    ANALYSIS_TEMPLATE_NUMBER_FIELD,
    ANALYSIS_TEMPLATE_INTEGER_FIELD,
    ANALYSIS_TEMPLATE_BOOLEAN_FIELD,
    ANALYSIS_TEMPLATE_DATE_FIELD,
    ANALYSIS_TEMPLATE_PLATE_ARRAY_FIELD,
  ],
  fields: [
    ASSAY_SCHEMA_FIELD,
    ANALYSIS_TEMPLATE_STRING_FIELD,
    ANALYSIS_TEMPLATE_NUMBER_FIELD,
    ANALYSIS_TEMPLATE_INTEGER_FIELD,
    ANALYSIS_TEMPLATE_BOOLEAN_FIELD,
    ANALYSIS_TEMPLATE_DATE_FIELD,
    ANALYSIS_TEMPLATE_PLATE_ARRAY_FIELD,
  ],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
  analysisTemplate: {id: ASSAY_RUN_ANALYSIS_TEMPLATE.id},
};

export const RUN_SCHEMA_WITH_ANALYSIS_TEMPLATE_GQL: RunSchemaGraphQL = {
  ...DEFAULT_RUN_SCHEMA_GQL_PROPS,
  __typename: 'RunSchemaNode',
  id: 'assaysch_with_template',
  name: 'Run Schema with Analysis Template',
  sqlIdentifier: 'run_with_template',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organizationJson: ORGANIZATION,
  allFieldsJson: [ASSAY_SCHEMA_FIELD, ANALYSIS_TEMPLATE_STRING_FIELD, ANALYSIS_TEMPLATE_NUMBER_FIELD],
  fieldsJson: [ASSAY_SCHEMA_FIELD, ANALYSIS_TEMPLATE_STRING_FIELD, ANALYSIS_TEMPLATE_NUMBER_FIELD],
  archiveRecord: null,
  analysisTemplate: {
    __typename: 'AnalysisTemplateNode',
    id: ASSAY_RUN_ANALYSIS_TEMPLATE.id,
  },
};

// Keep in sync with assayRecords.ts
export const ASSAY_SCHEMAS_BY_ID: {[id: string]: AssaySchema} = {
  assaysch_1: FLOW_RUN_SCHEMA,
  assaysch_2: CELL_SAMPLE_PREP_RUN_SCHEMA,
  assaysch_3: NGS_RUN_SCHEMA,
  assaysch_4: FLOW_CYTOMETRY_RESULT,
  assaysch_with_template: RUN_SCHEMA_WITH_ANALYSIS_TEMPLATE,
};

export const RESULT_SCHEMA: ResultSchema = {
  id: 'assaysch_result_1',
  name: 'Result Schema 1',
  requestTaskSchemaIds: [],
  sampleGroupSchema: null,
  sqlIdentifier: 'an_ending',
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  organization: ORGANIZATION,
  allFields: [],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

export const RESULT_SCHEMA_2: ResultSchema = {
  allFields: [ENTRY_LINK_FIELD, BATCH_LINK_FIELD],
  fields: [ENTRY_LINK_FIELD, BATCH_LINK_FIELD],
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  organization: ORGANIZATION,
  prefix: '',
  publishedDataTableColumns: undefined,
  requestTaskSchemaIds: [],
  requestTemplateIds: [],
  id: 'assay_result_schema_2',
  name: 'Result Schema 2',
  sqlIdentifier: 'result_schema_2',
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

export const RESULT_SCHEMA_WITH_REQUIRED_FIELD: ResultSchema = {
  id: 'assaysch_result_required',
  name: 'Result Schema with Required Field',
  requestTaskSchemaIds: [],
  sampleGroupSchema: null,
  sqlIdentifier: 'an_ending',
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  organization: ORGANIZATION,
  allFields: [REQUIRED_FIELD],
  fields: [REQUIRED_FIELD],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

export const RESULT_SCHEMA_GQL: ResultSchemaGraphQL = {
  __typename: 'ResultSchemaNode',
  id: 'assaysch_result_1',
  name: 'Result Schema 1',
  sqlIdentifier: 'an_ending',
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  organizationJson: ORGANIZATION,
  allFieldsJson: [],
  fieldsJson: [],
  publishedDataTableColumnsJson: null,
  archiveRecord: null,
  permissions: ASSAY_SCHEMA_PERMISSIONS_GQL,
};

export const RESULT_SCHEMA_WITH_FIELD_GQL: ResultSchemaGraphQL = {
  ...RESULT_SCHEMA_GQL,
  allFieldsJson: [ASSAY_SCHEMA_FIELD],
};

export const ARCHIVED_RESULT_SCHEMA_GQL: ResultSchemaGraphQL = {
  ...RESULT_SCHEMA_GQL,
  id: 'assaysch_archived',
  name: 'Archived Result Schema',
  archiveRecord: ARCHIVE_RECORD_GQL,
};

export const RUN_SCHEMA: AssayRunSchema & {robotInstructionsConfig: AutomationInputFileConfig | null} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_run_1',
  name: 'Run Schema 1',
  requestTaskSchemaIds: [],
  sampleGroupSchema: null,
  sqlIdentifier: 'overdue',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  requestTemplateIds: [],
  prefix: '',
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

export const RUN_SCHEMA_GQL: RunSchemaGraphQL = {
  __typename: 'RunSchemaNode',
  id: 'assaysch_run_1',
  name: 'Run Schema 1',
  sqlIdentifier: 'overdue',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organizationJson: ORGANIZATION,
  allFieldsJson: [],
  fieldsJson: [],
  archiveRecord: null,
  permissions: ASSAY_SCHEMA_PERMISSIONS_GQL,
  automationInputFileConfigsJson: [],
  automationOutputFileConfigsJson: [],
  robotInstructionsConfigJson: null,
  allowCreateRunFromNlad: true,
  allowInsertRunFromInbox: true,
  analysisTemplate: null,
  analysisTemplateVariableMapping: null,
  analysisTemplateDefaultFiltersMapping: null,
  adapterId: null,
  shouldRecordEquipment: false,
};

export const ARCHIVED_RESULT_SCHEMA: ResultSchema = {
  ...RESULT_SCHEMA,
  id: 'assaysch_archived_result_1',
  name: 'Archived Result Schema 1',
  archiveRecord: ARCHIVE_RECORD,
};

export const ARCHIVED_RUN_SCHEMA: AssayRunSchema & {
  robotInstructionsConfig: AutomationInputFileConfig | null;
} = {
  ...RUN_SCHEMA,
  id: 'assaysch_archived_run_1',
  name: 'Archived Run Schema 1',
  archiveRecord: ARCHIVE_RECORD,
};

export const ARCHIVED_RUN_SCHEMA_GQL: RunSchemaGraphQL = {
  ...RUN_SCHEMA_GQL,
  id: 'assaysch_archived_run_1',
  name: 'Archived Run Schema 1',
  archiveRecord: ARCHIVE_RECORD_GQL,
};

export const RESULT_SCHEMA_WITH_ARCHIVED_FIELD: ResultSchema = {
  id: 'assaysch_5',
  name: 'Flow Cytometry Result',
  requestTaskSchemaIds: [],
  sampleGroupSchema: null,
  sqlIdentifier: 'flow_cytometry_result',
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  organization: ORGANIZATION,
  allFields: [ARCHIVED_FIELD],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  requestTemplateIds: [],
  prefix: null,
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

export const RUN_SCHEMA_WITH_ARCHIVED_FIELD: AssayRunSchema & {
  robotInstructionsConfig: AutomationInputFileConfig | null;
} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_6',
  name: 'Flow Cytometry Run',
  requestTaskSchemaIds: [],
  sampleGroupSchema: null,
  sqlIdentifier: 'flow_cytometry_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [ASSAY_SCHEMA_FIELD, ARCHIVED_FIELD],
  fields: [ASSAY_SCHEMA_FIELD],
  publishedDataTableColumns: null,
  archiveRecord: null,
  requestTemplateIds: [],
  prefix: null,
};

export const UNCREATEABLE_RUN_SCHEMA = {
  ...RUN_SCHEMA,
  id: 'assaysch_run_29123993',
  name: 'Uncreateable Run Schema',
  permissions: {
    ...ASSAY_SCHEMA_PERMISSIONS_GQL,
    objectsCreatable: false,
  },
};

export const UNCREATABLE_RUN_SCHEMA_GQL: RunSchemaGraphQL = {
  ...RUN_SCHEMA_GQL,
  __typename: 'RunSchemaNode',
  id: 'assaysch_run_29123993',
  name: 'Uncreateable Run Schema',
  permissions: {...ASSAY_SCHEMA_PERMISSIONS_GQL, objectsCreatable: false},
};

export const UNCREATEABLE_RESULT_SCHEMA: ResultSchema = {
  ...RESULT_SCHEMA,
  id: 'assaysch_result_29123993',
  name: 'Uncreateable Result Schema',
  permissions: {
    ...ASSAY_SCHEMA_PERMISSIONS,
    objectsCreatable: false,
  },
};

export const UNCREATABLE_RESULT_SCHEMA_GQL: ResultSchemaGraphQL = {
  ...UNCREATEABLE_RESULT_SCHEMA,
  __typename: 'ResultSchemaNode',
  allFieldsJson: null,
  archiveRecord: null,
  fieldsJson: null,
  organizationJson: null,
  permissions: {...ASSAY_SCHEMA_PERMISSIONS_GQL, objectsCreatable: false},
  publishedDataTableColumnsJson: null,
};
